import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Plug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EnvironmentBadge } from '@/components/environment-badge';
import { Field } from '@/components/field';
import { Logo } from '@/components/logo';
import { ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation, useMe } from '@/lib/hooks';
import { t } from '@/lib/i18n';

/**
 * `/connect?request=…`: an app (Claude, ChatGPT, …) asks for access to Deskworks's MCP server by OAuth. The person
 * picks the Account and whether the app may write; the app then works with their permissions in every workspace of it.
 */
export function ConnectPage() {
  const { request } = useSearch({ from: '/_authed/connect' });
  const me = useMe();
  const described = useQuery({
    queryKey: ['oauthConsent', request],
    queryFn: () => api.mcpApps.describeRequest(request ?? ''),
    enabled: !!request,
    retry: false,
  });
  const accounts = me.data?.accounts ?? [];
  const [accountId, setAccountId] = useState('');
  const [write, setWrite] = useState(true);
  useEffect(() => {
    if (!accountId && accounts.length > 0) setAccountId(accounts[0].id);
  }, [accountId, accounts]);

  const decide = useApiMutation(
    (allow: boolean) => api.mcpApps.decide({ request: request ?? '', allow, accountId: allow ? accountId : undefined, write }),
    { onSuccess: (res) => window.location.assign(res.redirectUrl) },
  );

  const app = described.data;
  const canWrite = !!app?.scopes.includes('write');

  return (
    <div className="flex min-h-full flex-col items-center bg-background px-4 py-10 sm:justify-center sm:bg-muted/40">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <Logo className="size-10" />
          <EnvironmentBadge />
        </div>
        <Card>
          <CardContent className="grid gap-5 pt-6">
            {!request ? (
              <ErrorState title={t('This link is incomplete')} error={new Error(t('Start connecting again in your app.'))} />
            ) : described.isError || me.isError ? (
              <ErrorState error={described.error ?? me.error} />
            ) : !app || me.isPending ? (
              <LoadingRows rows={3} />
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Plug className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-lg font-semibold tracking-tight">{t('Connect “{name}” to Deskworks?', { name: app.clientName })}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('The app reaches Deskworks through MCP with your permissions — in every workspace of the account you choose.')}
                    </p>
                  </div>
                </div>
                {accounts.length > 1 ? (
                  <Field label={t('Account')}>
                    <Select value={accountId} onValueChange={setAccountId}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                ) : (
                  accounts[0] && (
                    <p className="text-sm">
                      {t('Account')}: <span className="font-medium">{accounts[0].name}</span>
                    </p>
                  )
                )}
                <div className="grid gap-2">
                  <Label>{t('Permissions')}</Label>
                  <label className="flex items-start gap-2.5 rounded-md border px-3 py-2.5">
                    <Checkbox checked disabled className="mt-0.5" />
                    <span className="text-sm">
                      <span className="font-medium">{t('Read')}</span>
                      <span className="block text-xs text-muted-foreground">{t('View desks, work items, agents and open approvals. Always included.')}</span>
                    </span>
                  </label>
                  {canWrite && (
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5">
                      <Checkbox checked={write} onChange={(e) => setWrite(e.target.checked)} className="mt-0.5" />
                      <span className="text-sm">
                        <span className="font-medium">{t('Write')}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t('Create and run desks, create work items, send messages, give approvals.')}
                        </span>
                      </span>
                    </label>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('Afterwards you go back to {origin}. You can disconnect the app any time in Settings → MCP access.', { origin: app.redirectOrigin })}
                </p>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button variant="outline" onClick={() => decide.mutate(false)} disabled={decide.isPending}>
                    {t('Deny')}
                  </Button>
                  <Button onClick={() => decide.mutate(true)} loading={decide.isPending} disabled={!accountId}>
                    {t('Allow')}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
